import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { Validators, UntypedFormGroup, UntypedFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '../auth.service';
import { NavController, ModalController, IonicModule, Platform } from '@ionic/angular';
import { CountriesPage } from '../../countries/countries.page';
import { ToastService } from '../../_core/services/toast.service';
import { EncrptionService } from '../encryption/encryption.service';
import { IS_NEW_UI_FLOW, PUBLIC_KEY } from '../../../environments/environment';
import { RecaptchaModule, ReCaptchaV3Service } from 'ng-recaptcha';
import { finalize, take } from 'rxjs';
import { Router } from '@angular/router';
import { images } from '../../_shared/data/image';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-forgot-password',
    templateUrl: './forgot-password.component.html',
    styleUrls: ['./forgot-password.component.scss'],
    standalone: true,
    imports: [
        IonicModule,
        ReactiveFormsModule,
        RecaptchaModule,
        CommonModule,
        TranslateModule
    ]
})
export class ForgotPasswordComponent implements OnInit {
    @Input({ required: false }) isLoginModal = false;
    @Output() goBackPage = new EventEmitter<string>();
    private platform = inject(Platform);
    getCodeFormGroup: UntypedFormGroup;
    changePasswordFormGroup: UntypedFormGroup;
    haveCode = false;
    isNewLayout: boolean = true;
    public logoSquare = images.logoSquare;
    selectedCountryCode: string = '+1';
    public showPassword: boolean = false;
    route = IS_NEW_UI_FLOW ? '/auth' : '/login';
    public IS_NEW_UI_FLOW = IS_NEW_UI_FLOW;

    private normalizeEmail(value: string | null | undefined): string {
        return value?.trim().toLowerCase() ?? '';
    }

    constructor(
        private fb: UntypedFormBuilder,
        private authService: AuthService,
        private navCtrl: NavController,
        private modalCtrl: ModalController,
        private toast: ToastService,
        private encryptionService: EncrptionService,
        private recaptchaV3Service: ReCaptchaV3Service,
        private router: Router
    ) { }

    ngOnInit() {
        this.buildForm();
        this.isNewLayout = this.isLoginModal || this.router.url.includes('login/forgot-password') || this.router.url.includes('auth/forgot-password');;
    }

    buildForm() {
        this.getCodeFormGroup = this.fb.group({
            country_code: ['+1', [Validators.required, Validators.pattern('([+]{1})([0-9]{1,3})')]],
            phone: ['', [Validators.pattern('([1-9]{1})[0-9]{5,15}')]],
            email: ['', [Validators.email]],
        });

        this.changePasswordFormGroup = this.fb.group({
            country_code: ['+1', [Validators.required, Validators.pattern('([+]{1})([0-9]{1,3})')]],
            phone: ['', [Validators.pattern('([1-9]{1})[0-9]{5,15}')]],
            email: ['', [Validators.email]],
            code: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(6)]],
            password: ['', [Validators.required, Validators.minLength(6)]]
        });

    }

    encryptPhoneNumber(phoneNumber: number) {
        const encryptedData = this.encryptionService.encryptPhoneWithRSA_AES(phoneNumber.toString(), PUBLIC_KEY);
        return encryptedData;
    }

    getCode() {
        if (this.isNewLayout) {
            const email = this.normalizeEmail(this.getCodeFormGroup.get('email')?.value);
            if (email) {
                this.getCodeFormGroup.patchValue({ email }, { emitEvent: false });
            }
            const payload = {
                country_code: this.selectedCountryCode,
                emailOrPhone: email || this.getCodeFormGroup.get('phone')?.value?.trim(),
            };

            this.authService.forgotPassword(payload)
                .pipe(take(1))
                .subscribe({
                    next: (res) => {
                        this.toast.presentToast(res.message);
                        this.changePasswordFormGroup.patchValue({ emailOrPhone: payload.emailOrPhone });
                        this.changeForm();
                    },
                    error: (responseError) => {
                        if (responseError.message) {
                            this.toast.presentDangerToast(responseError.message);
                        }
                    }
                });
        } else {
            this.recaptchaV3Service
                .execute('forgotPassword')
                .pipe(take(1))
                .subscribe((recaptchaToken) => {
                    const phone = this.encryptPhoneNumber(this.getCodeFormGroup.get('phone')?.value);
                    const forgotPassRequest = {
                        phone: phone.encryptedPhone,
                        secret_key: phone.encryptedRSAKey,
                        country_code: this.selectedCountryCode,
                        captcha_token: recaptchaToken
                    };

                    this.authService.getCode(forgotPassRequest)
                        .pipe(take(1))
                        .subscribe({
                            next: (res) => {
                                this.toast.presentToast(res.message);
                                this.changePasswordFormGroup.patchValue({ country_code: this.getCodeFormGroup.get('country_code')?.value });
                                this.changePasswordFormGroup.patchValue({ phone: this.getCodeFormGroup.get('phone')?.value });
                                this.changeForm();
                            },
                            error: (responseError) => {
                                if (responseError.message) {
                                    this.toast.presentToast(responseError.message);
                                }
                            }
                        });
                });
        }
    }

    changePassword() {
        if (this.isNewLayout) {
            const email = this.normalizeEmail(this.changePasswordFormGroup.get('email')?.value);
            if (email) {
                this.changePasswordFormGroup.patchValue({ email }, { emitEvent: false });
            }
            const verifyData = {
                country_code: this.selectedCountryCode,
                emailOrPhone: email || this.changePasswordFormGroup.get('phone')?.value,
                code: this.changePasswordFormGroup.get('code')?.value.trim(),
                password: this.changePasswordFormGroup.get('password')?.value
            };

            this.authService.changeCognitoPassword(verifyData).subscribe({
                next: async (responseSuccess) => {
                    this.toast.presentToast('Password changed successfully');
                    if (this.isLoginModal) {
                        this.modalDismiss();
                    } else {
                        this.navCtrl.navigateForward(`/${this.route}`, {
                            state: { showLogin: true }
                        });
                    }
                },
                error: (responseError) => {
                    if (responseError.message) {
                        this.toast.presentDangerToast(responseError?.error?.message ?? responseError?.message);
                    } else {
                        console.error('Password change error:', responseError);
                    }
                }
            });

        } else {
            const { emailOrPhone, ...changePasswordPayload } = this.changePasswordFormGroup.value;
            const params = {
                ...changePasswordPayload,
                country_code: this.selectedCountryCode,
            }
            this.authService.changePassword(params).subscribe({
                next: (responseSuccess) => {
                    const pendingEmail = localStorage.getItem('pendingEmail');
                    const pendingPhone = localStorage.getItem('pendingPhone');
                    const countrycode = localStorage.getItem('pendingCountryCode');
                    const phone = countrycode + this.changePasswordFormGroup.get('phone')?.value
                    if (pendingEmail && pendingPhone === phone) {
                        this.router.navigateByUrl('/login');
                        window.location.reload();
                    } else if (pendingEmail && pendingPhone !== phone) {
                        localStorage.removeItem('pendingEmail');
                        localStorage.removeItem('pendingPhone');
                        localStorage.removeItem('pendingCountryCode');
                        this.navCtrl.navigateRoot('/');
                        setTimeout(() => {
                            window.location.reload();
                        }, 200);
                    } else {
                        this.navCtrl.navigateRoot('/');
                    }
                },
                error: (responseError) => {
                    //console.log(responseError);
                }
            });
        }
    }

    changeForm() {
        this.haveCode = !this.haveCode;
    }

    async getCountryCode() {
        const modal = await this.modalCtrl.create({
            component: CountriesPage,
            componentProps: {
                showCountryName: true,
            }
        });

        await modal.present();
        const { data } = await modal.onDidDismiss();
        if (data?.country) {
            this.selectedCountryCode = data.country;
            this.getCodeFormGroup.patchValue({ country_code: data.country });
            this.changePasswordFormGroup.patchValue({ country_code: data.country });
        }
    }

    get screenHeight(): number {
        return this.platform.height();
    }

    isChangePasswordFormValid(): boolean {
        if (!this.changePasswordFormGroup) {
            return false;
        }

        const form = this.changePasswordFormGroup;
        const countryCode = form.get('country_code')?.value?.trim();
        const email = form.get('email')?.value?.trim();
        const phone = form.get('phone')?.value?.trim();
        const code = form.get('code')?.value?.trim();
        const password = form.get('password')?.value?.trim();

        const phoneRegex = /^[1-9][0-9]{5,14}$/;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        const isPhoneValid = phone && phoneRegex.test(phone);
        const isEmailValid = email && emailRegex.test(email);
        const isCodeValid = code && code.length >= 4 && code.length <= 6;
        const isPasswordValid = password && password.length >= 6;

        return (
            !!countryCode &&
            (isEmailValid || isPhoneValid) &&
            isCodeValid &&
            isPasswordValid
        );
    }

    isGetCodeFormValid(): boolean {
        if (!this.getCodeFormGroup) {
            return false;
        }

        const form = this.getCodeFormGroup;
        const email = form.get('email')?.value?.trim();
        const phone = form.get('phone')?.value?.trim();
        const countryCode = form.get('country_code')?.value?.trim();

        const phoneRegex = /^[1-9][0-9]{5,14}$/;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        const isPhoneValid = phone && phoneRegex.test(phone);
        const isEmailValid = email && emailRegex.test(email);

        return isEmailValid || (isPhoneValid && !!countryCode);
    }

    isPasswordLengthValid(): boolean {
        return (this.changePasswordFormGroup.get('password')?.value || '').length >= 8;
    }

    getPasswordStrength(): string {
        const password = this.changePasswordFormGroup.get('password')?.value;
        if (!password) return 'weak';

        let strength = 0;
        if (password.length >= 8) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/\d/.test(password)) strength++;

        if (strength >= 4) return 'strong';
        if (strength >= 3) return 'medium';
        return 'weak';
    }

    isPasswordCaseValid(): boolean {
        const value = this.changePasswordFormGroup.get('password')?.value || '';
        return /[a-z]/.test(value) && /[A-Z]/.test(value);
    }

    isPasswordNumberValid(): boolean {
        return /\d/.test(this.changePasswordFormGroup.get('password')?.value || '');
    }

    togglePasswordVisibility() {
        this.showPassword = !this.showPassword;
    }

    modalDismiss() {
        this.modalCtrl.dismiss();
    }

    goBack() {
        this.goBackPage.emit('signin');
    }

}
